import React, { useState, useEffect, useRef } from 'react';
import { useDashboard } from '../DashboardContext';

/* Shown the moment a client changes anything on their advisor's plan.
   That plan is the benchmark and cannot be saved over, so rather than let
   the edit sit in the UI and quietly vanish on reload, we offer to move it
   into an option the client owns. */
export default function ForkPrompt() {
  const { planMgr } = useDashboard();
  const [name, setName] = useState('');
  const [busy, setBusy] = useState(false);
  const inputRef = useRef(null);

  const open = !!planMgr?.pendingFork;
  const suggested = planMgr?.nextOptionName ? planMgr.nextOptionName() : 'My Option 1';

  // Seed the name once per opening. `suggested` is deliberately not a
  // dependency: recomputing it mid-edit would overwrite what the client typed.
  useEffect(() => {
    if (!open) return undefined;
    setName(suggested);
    setBusy(false);
    // Let the dialog mount before focusing, so the caret lands correctly.
    const t = setTimeout(() => inputRef.current?.select(), 30);
    return () => clearTimeout(t);
  }, [open]);

  if (!open) return null;

  const { confirmFork, discardEdits, activePlan } = planMgr;

  const create = async () => {
    setBusy(true);
    const ok = await confirmFork(name);
    if (!ok) setBusy(false); // stay open so the client can retry
  };

  const onKeyDown = (e) => {
    if (e.key === 'Enter' && !busy) create();
    if (e.key === 'Escape' && !busy) discardEdits();
  };

  return (
    <div
      className="no-print"
      role="dialog"
      aria-modal="true"
      aria-labelledby="fork-title"
      style={{
        position: 'fixed', inset: 0, zIndex: 100,
        background: 'rgba(26,26,26,0.45)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        padding: 24,
      }}
    >
      <div
        onKeyDown={onKeyDown}
        style={{
          width: '100%', maxWidth: 460,
          background: '#FFFFFF',
          border: '1px solid #D4D4D4',
          borderLeft: '6px solid #92700C',
          borderRadius: 8,
          padding: '28px 32px',
          fontFamily: "'Outfit', sans-serif",
        }}
      >
        <div style={{
          fontFamily: "'IBM Plex Mono', monospace", fontSize: 10, fontWeight: 600,
          color: '#92700C', textTransform: 'uppercase', letterSpacing: '0.22em',
          marginBottom: 10,
        }}>
          Your advisor&apos;s plan
        </div>

        <h2 id="fork-title" style={{
          fontSize: 22, fontWeight: 600, color: '#1A1A1A',
          letterSpacing: '-0.01em', margin: '0 0 10px', lineHeight: 1.25,
        }}>
          Save this as your own option?
        </h2>

        <p style={{ fontSize: 14, fontWeight: 300, lineHeight: 1.6, color: '#525252', margin: '0 0 20px' }}>
          {activePlan?.name ? `"${activePlan.name}" is ` : 'This plan is '}
          the version MinerOps built for you, so it stays as the benchmark and cannot be changed.
          Your edits can go into an option of your own, which you can adjust freely and compare
          against the original.
        </p>

        <label
          htmlFor="fork-name"
          style={{
            display: 'block', fontFamily: "'IBM Plex Mono', monospace", fontSize: 10,
            fontWeight: 600, color: '#737373', textTransform: 'uppercase',
            letterSpacing: '0.14em', marginBottom: 7,
          }}
        >
          Name this option
        </label>
        <input
          id="fork-name"
          ref={inputRef}
          value={name}
          onChange={(e) => setName(e.target.value)}
          disabled={busy}
          style={{
            width: '100%', boxSizing: 'border-box',
            padding: '11px 14px', marginBottom: 22,
            background: '#FFFFFF', border: '1px solid #D4D4D4', borderRadius: 4,
            fontSize: 14, color: '#1A1A1A', fontFamily: "'Outfit', sans-serif",
          }}
          onFocus={(e) => { e.target.style.borderColor = '#92700C'; }}
          onBlur={(e) => { e.target.style.borderColor = '#D4D4D4'; }}
        />

        <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', flexWrap: 'wrap' }}>
          <button
            onClick={discardEdits}
            disabled={busy}
            style={{
              padding: '11px 20px', borderRadius: 4, cursor: busy ? 'default' : 'pointer',
              background: 'transparent', border: '1px solid #D4D4D4', color: '#525252',
              fontSize: 14, fontWeight: 500, fontFamily: "'Outfit', sans-serif",
              opacity: busy ? 0.5 : 1,
            }}
          >
            Discard my changes
          </button>
          <button
            onClick={create}
            disabled={busy || !name.trim()}
            style={{
              padding: '11px 24px', borderRadius: 4,
              cursor: busy || !name.trim() ? 'default' : 'pointer',
              background: busy || !name.trim() ? '#E5E5E5' : '#1A1A1A',
              color: busy || !name.trim() ? '#999999' : '#FCFCFC',
              border: 'none', fontSize: 14, fontWeight: 500,
              fontFamily: "'Outfit', sans-serif",
            }}
          >
            {busy ? 'Creating…' : 'Create my option'}
          </button>
        </div>

        <p style={{ fontSize: 12, color: '#999999', margin: '16px 0 0', lineHeight: 1.5 }}>
          Discarding restores your advisor&apos;s plan exactly as they saved it. Nothing you do
          here can change their version.
        </p>
      </div>
    </div>
  );
}
